import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import Stamp from "@/components/common/Stamp";
import { PRODUCTS, SITE_URL } from "@/data/staticContent";
import { SITE_URL as URL_SITE } from "@/lib/api";

export default function Productos() {
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Catálogo de Autodestrucción S.A.",
        description:
            "Productos / personajes corporativos del catálogo satírico: Alcohol, Cocaína, Pasta Base, Compañía Rota. Representación crítica del consumo.",
        itemListElement: PRODUCTS.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.name,
            description: p.tagline,
        })),
    };

    return (
        <>
            <SEO
                title="Productos · Catálogo del vacío"
                description="Nuestro catálogo de productos satíricos: Alcohol, Cocaína, Pasta Base y Compañía Rota. Representación crítica del consumo, sin glorificar la adicción."
                path="/productos"
                jsonLd={jsonLd}
                keywords="adicción, sustancias, sátira, alcohol, cocaína, pasta base, compañía rota, crítica del consumo"
            />

            {/* Hero */}
            <section className="grain relative py-20 md:py-28 border-b border-[var(--line)]">
                <div className="container-x">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)]">
                            Sección 01 · Catálogo
                        </span>
                        <span className="dotted-div flex-1" />
                        <Stamp size="sm" rotation={-4}>
                            Confidencial
                        </Stamp>
                    </div>
                    <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight mb-6">
                        Nuestros{" "}
                        <span className="text-[var(--danger)]">productos</span>
                    </h1>
                    <p className="font-mono text-base md:text-lg text-[var(--paper)] max-w-2xl leading-relaxed">
                        Entrega 24/7. Experiencia: 20 años garantizando
                        resultados destructivos. Lo que vendemos no es una
                        sustancia: es una vida vacía con servicio técnico
                        permanente.
                    </p>
                </div>
            </section>

            {/* Products grid */}
            <section className="py-16 md:py-24" data-testid="productos-list">
                <div className="container-x">
                    <div className="grid lg:grid-cols-2 gap-10 md:gap-14">
                        {PRODUCTS.map((p, i) => (
                            <article
                                key={p.slug}
                                className="dossier hover:border-[var(--danger)] transition-colors"
                                data-testid={`producto-${p.slug}`}
                                id={p.slug}
                            >
                                <div className="dossier-header">
                                    <span>{p.codeName}</span>
                                    <span className="text-[var(--danger)]">
                                        {p.warning}
                                    </span>
                                </div>
                                <div className="grid sm:grid-cols-2">
                                    <div className="aspect-square overflow-hidden bg-black relative">
                                        <img
                                            src={p.image}
                                            alt={`Representación conceptual oscura de ${p.name}, parte del catálogo crítico de Autodestrucción S.A.`}
                                            className="w-full h-full object-cover opacity-90"
                                            loading="lazy"
                                        />
                                        <div className="absolute top-4 right-4">
                                            <Stamp size="md" rotation={-8 + i * 3}>
                                                AD-{String(i + 1).padStart(3, "0")}
                                            </Stamp>
                                        </div>
                                    </div>
                                    <div className="p-6 md:p-8 flex flex-col justify-between">
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mb-2">
                                                {p.role}
                                            </p>
                                            <h2 className="font-display text-4xl mb-3 text-[var(--paper-2)]">
                                                {p.name}
                                            </h2>
                                            <p className="font-mono text-sm text-[var(--warning)] mb-4 leading-relaxed italic">
                                                "{p.tagline}"
                                            </p>
                                            <p className="font-mono text-xs text-[var(--paper)] leading-relaxed">
                                                {p.description}
                                            </p>
                                        </div>
                                        <div className="mt-6 pt-4 border-t border-dashed border-[var(--ink-dim)]">
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)] flex items-center gap-2">
                                                <AlertTriangle className="w-3 h-3" />
                                                {p.warning}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-[var(--warning)] text-black border-y-2 border-black">
                <div className="container-x text-center">
                    <h2 className="font-display text-3xl md:text-5xl mb-4">
                        Si esto dejó de parecer chiste…
                    </h2>
                    <p className="font-mono text-sm md:text-base mb-6 max-w-xl mx-auto">
                        Es porque ya no lo es. La cicatriz también pide ayuda
                        cuando se cansa de escribir.
                    </p>
                    <Link
                        to="/salida"
                        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-black text-[var(--warning)] px-8 py-4 hover:bg-[#1a1a1a]"
                        data-testid="productos-salida-cta"
                    >
                        Salida de emergencia
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </>
    );
}
